import React, { useEffect, useContext } from 'react'
import { useLocation } from 'react-router-dom'
import '../../assets/stylesheets/header-style.css'
import '../../assets/stylesheets/shepherd.css'
import { AccountModal } from '../Gallery/Modal'
import { useLGDetection } from '../LG/hooks/useLGDetection'
import { Message } from 'semantic-ui-react'
import { LGModal } from '../LG'
import { ShepherdTourContext } from 'react-shepherd'
import {
  useHeaderNavigation,
  useMenuManagement,
  useMenuTimeouts,
  useHeaderSearch,
  useAuthManagement,
  useHeaderState
} from './hooks'
import {
  DesktopNavigation,
  MobileNavigation,
  HamburgerMenu,
  AccountMenu,
  HeaderLogo,
  NotificationPopup
} from './components'

const loggedOutHeader = props => (
  <div className="header">
    <a href="/gallery">
      <HeaderLogo variant="default" />
    </a>
    <div className="desktop-home-nav-container">
      <button
        id="signup-login-button"
        className="button-med primary"
        onClick={e => props.handleNavigation(e, '/auth')}
      >
        signup / login
      </button>
    </div>
    <div
      className={`mobile-signin-pane ${
        props.isSignInHamburgerMenuVisible ? 'active' : ''
      }`}
    >
      <ul id="signin-menu" className="shadow04">
        <li
          className="primary-font bold mobile-top-level-link01"
          onClick={e => props.handleSignInNavigation(e, '/auth')}
        >
          signup / login
        </li>
      </ul>
    </div>

    <HamburgerMenu
      variant="logged-out"
      onClick={e => props.handleSignInHamburger(e)}
    />
  </div>
)

const loggedInHeader = ({
  handleSearchSubmit,
  searchTerm,
  handleSearchInput,
  ...props
}) => (
  <div className="header-popup">
    <div className="header">
      <HamburgerMenu
        variant="logged-in"
        onClick={e => props.handleHamburger(e)}
      />
      <HeaderLogo
        variant="left"
        onClick={e => props.handleNavigation(e, '/gallery')}
      />
      <DesktopNavigation
        searchTerm={searchTerm}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchInput={handleSearchInput}
        toggleViewFilesMenuVisibility={props.toggleViewFilesMenuVisibility}
        isViewFilesMenuVisible={props.isViewFilesMenuVisible}
        handleNavigation={props.handleNavigation}
        handleDuplicatesClick={props.handleDuplicatesClick}
        handleFavoritesClick={props.handleFavoritesClick}
        handleDeletedClick={props.handleDeletedClick}
        toggleCreateKinCategoryMenuVisibility={
          props.toggleCreateKinCategoryMenuVisibility
        }
        isCreateKinCategoryMenuVisible={props.isCreateKinCategoryMenuVisible}
        handleKinNavigation={props.handleKinNavigation}
        closeAllMenus={props.closeAllMenus}
        handleBuildYourOwnClick={props.handleBuildYourOwnClick}
        isSubNavVisible={props.isSubNavVisible}
        handleUploadClick={props.handleUploadClick}
        toggleAccountMenuVisibility={props.toggleAccountMenuVisibility}
      />
      <MobileNavigation
        isMobileTopLevelMenuVisible={props.isMobileTopLevelMenuVisible}
        handleMainCategoryClick={props.handleMainCategoryClick}
        activeMainCategory={props.activeMainCategory}
        handleNavigation={props.handleNavigation}
        handleDuplicatesClick={props.handleDuplicatesClick}
        handleFavoritesClick={props.handleFavoritesClick}
        handleDeletedClick={props.handleDeletedClick}
        handleKinNavigation={props.handleKinNavigation}
        closeAllMenus={props.closeAllMenus}
        handleUploadClick={props.handleUploadClick}
      />
      <ul>
        {/* Other list items */}
        {/* Other list items */}
      </ul>
    </div>
    <NotificationPopup
      showNotification={props.showNotification}
      welcomeMessage={props.welcomeMessage}
      setShowNotification={props.setShowNotification}
    />
  </div>
)

//////////////////////////////////////////////////// MAIN HEADER COMPONENT //////////////////////////////////////////////////////

function Header(props) {
  const tour = useContext(ShepherdTourContext)
  const location = useLocation()

  // LG TV detection
  const { isLGSmartTV } = useLGDetection()

  // Use consolidated state for notifications and welcome message management
  const { notifications, setNotification, clearNotification } = useHeaderState()

  const {
    handleNavigation,
    handleKinNavigation,
    handleSignInNavigation,
    handleUploadClick,
    handleDuplicatesClick,
    handleFavoritesClick,
    handleDeletedClick,
    handleSubscribe,
    handleLogoClick,
    handleKategoriesClick
  } = useHeaderNavigation()

  const {
    isViewFilesMenuVisible,
    isHamburgerMenuVisible,
    isAccountMenuVisible,
    isMobileTopLevelMenuVisible,
    isSignInHamburgerMenuVisible,
    isCreateKinCategoryMenuVisible,
    activeMainCategory,
    isSubNavVisible,
    isAccountModalVisible,
    isLGModalOpen,
    toggleViewFilesMenuVisibility,
    toggleAccountMenuVisibility,
    toggleCreateKinCategoryMenuVisibility,
    toggleHamburgerMenuVisibility,
    toggleSignInHamburgerMenuVisibility,
    toggleAccountModal,
    toggleLGModal,
    handleBuildYourOwnClick,
    handleMainCategoryClick,
    closeAllMenus
  } = useMenuManagement()

  const { clearAllTimeouts } = useMenuTimeouts()

  const { searchTerm, handleSearchSubmit, handleSearchInput } =
    useHeaderSearch()

  const {
    user,
    handleLogout,
    handleExitToLG,
    handleHamburger,
    handleSignInHamburger
  } = useAuthManagement()

  // Notification management
  useEffect(() => {
    const welcomeMessage = isLGSmartTV ? (
      <Message info compact floating className="lg-tv-message">
        Welcome to Kinstak! Embark on your photo journey with ease. <br />
        Simply log in to your new Kinstak account from any device that houses
        your photos—be it a phone, tablet, or computer.
      </Message>
    ) : (
      <Message info compact floating>
        Welcome to Kinstak! Let's start your journey by uploading some photos.
      </Message>
    )

    if (location.pathname === '/upload') {
      setNotification(true, welcomeMessage)
    } else {
      setNotification(false, welcomeMessage)
    }
  }, [location, setNotification])

  // Enhanced handlers that integrate with menu management
  const handleEnhancedUploadClick = () => {
    handleUploadClick()
    closeAllMenus()
  }

  const handleEnhancedDuplicatesClick = e => {
    handleDuplicatesClick(e)
    closeAllMenus()
  }

  const handleEnhancedFavoritesClick = () => {
    handleFavoritesClick()
    closeAllMenus()
  }

  const handleEnhancedDeletedClick = () => {
    handleDeletedClick()
    closeAllMenus()
  }

  const handleEnhancedSubscribe = () => {
    handleSubscribe()
    closeAllMenus()
  }

  const handleEnhancedKinNavigation = (e, category, onClose) => {
    handleKinNavigation(e, category, onClose)
  }

  const handleEnhancedNavigation = (e, target) => {
    handleNavigation(e, target)
    closeAllMenus()
  }

  const handleEnhancedExitToLG = () => {
    handleExitToLG()
    toggleLGModal()
  }

  const handleSetShowNotification = value => {
    setNotification(value, notifications.welcomeMessage)
  }

  const handleEnhancedHamburger = e => {
    handleHamburger(e)
    toggleHamburgerMenuVisibility()
  }

  const handleEnhancedSignInHamburger = e => {
    handleSignInHamburger(e)
    toggleSignInHamburgerMenuVisibility()
  }

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      clearAllTimeouts()
    }
  }, [clearAllTimeouts])

  return (
    <>
      {user && (
        <AccountMenu
          isAccountMenuVisible={isAccountMenuVisible}
          toggleAccountModal={toggleAccountModal}
          handleSubscribe={handleEnhancedSubscribe}
          handleLogout={handleLogout}
          handleExitToLG={handleEnhancedExitToLG}
        />
      )}
      {user &&
        loggedInHeader({
          handleFavoritesClick: handleEnhancedFavoritesClick,
          handleDeletedClick: handleEnhancedDeletedClick,
          handleDuplicatesClick: handleEnhancedDuplicatesClick,
          handleBuildYourOwnClick,
          handleSubscribe: handleEnhancedSubscribe,
          handleLogoClick,
          handleExitToLG: handleEnhancedExitToLG,
          welcomeMessage: notifications.welcomeMessage,
          showNotification: notifications.showNotification,
          setShowNotification: handleSetShowNotification,
          isSubNavVisible,
          activeMainCategory,
          handleMainCategoryClick,
          isMobileTopLevelMenuVisible,
          isCreateKinCategoryMenuVisible,
          toggleViewFilesMenuVisibility,
          toggleCreateKinCategoryMenuVisibility,
          handleKategoriesClick,
          handleUploadClick: handleEnhancedUploadClick,
          toggleAccountMenuVisibility,
          isViewFilesMenuVisible,
          handleLogout,
          handleNavigation: handleEnhancedNavigation,
          handleKinNavigation: handleEnhancedKinNavigation,
          handleHamburger: handleEnhancedHamburger,
          isHamburgerMenuVisible,
          closeAllMenus,
          tour,
          searchTerm,
          handleSearchSubmit,
          handleSearchInput
        })}
      {!user &&
        loggedOutHeader({
          handleSignInNavigation,
          isSignInHamburgerMenuVisible,
          handleSignInHamburger: handleEnhancedSignInHamburger,
          handleNavigation: handleEnhancedNavigation,
          handleHamburger: handleEnhancedHamburger,
          isHamburgerMenuVisible
        })}
      {isAccountModalVisible && (
        <AccountModal
          isVisible={isAccountModalVisible}
          onClose={toggleAccountModal}
        />
      )}
      {isLGModalOpen && (
        <LGModal isVisible={isLGModalOpen} onClose={handleEnhancedExitToLG} />
      )}
    </>
  )
}

export default Header
